import React, { useState } from "react";
import { Captions, Plus, Trash2 } from "lucide-react";

const POS = [["top", "Top"], ["center", "Middle"], ["bottom", "Bottom"]];
const num = "w-14 h-8 rounded-md border border-slate-200 px-1.5 text-xs font-mono text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30";

const Row = ({ row, i, set, remove, onFocus }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid={`caption-row-${i}`}>
    <input value={row.text} onChange={(e) => set({ text: e.target.value })} onFocus={onFocus} maxLength={120} placeholder="Caption text" data-testid={`caption-text-${i}`}
      className="flex-1 min-w-[140px] h-8 rounded-md border border-slate-200 px-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
    <input type="number" min={0} step={0.5} value={row.start} onChange={(e) => set({ start: e.target.value })} aria-label="Start second" data-testid={`caption-start-${i}`} className={num} />
    <span className="text-[10px] text-slate-400">to</span>
    <input type="number" min={0} step={0.5} value={row.end} onChange={(e) => set({ end: e.target.value })} aria-label="End second" data-testid={`caption-end-${i}`} className={num} />
    <select value={row.position} onChange={(e) => set({ position: e.target.value })} data-testid={`caption-position-${i}`} className="h-8 rounded-md border border-slate-200 px-1 text-xs text-slate-700 bg-white">
      {POS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    <button onClick={remove} data-testid={`caption-remove-${i}`} aria-label="Remove caption" className="p-1.5 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={13} /></button>
  </div>
);

export const CaptionsCard = ({ video, draft, onSave, onFocusCaption }) => {
  const { rows, setRows, dirty } = draft;
  const [saving, setSaving] = useState(false);
  const len = video.length || 5;
  const set = (i, patch) => setRows((r) => r.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const add = () => {
    const last = rows[rows.length - 1];
    const start = last ? Math.min(Number(last.end) || 0, Math.max(0, len - 1)) : 0;
    const row = { id: null, text: "", start, end: Math.min(start + 3, len), position: "bottom" };
    setRows((r) => [...r, row]);
    onFocusCaption(row);
  };
  const save = async () => { setSaving(true); await onSave(); setSaving(false); };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="captions-card">
      <h3 className="text-sm font-medium text-slate-900 flex items-center gap-2"><Captions size={15} className="text-emerald-600" /> Captions <span className="text-[11px] font-normal text-slate-400">free</span></h3>
      {rows.map((r, i) => <Row key={i} row={r} i={i} set={(p) => set(i, p)} onFocus={() => onFocusCaption(r)} remove={() => setRows((x) => x.filter((_, k) => k !== i))} />)}
      {rows.length > 0
        ? <p className="text-[11px] text-slate-400" data-testid="captions-live-hint">Changes show in the preview as you type{dirty ? " — save to keep them" : ""}.</p>
        : <p className="text-xs text-slate-500" data-testid="captions-empty">Add text that appears on screen — times are in seconds of the final video.</p>}
      <div className="flex items-center gap-2">
        {rows.length < 20 && <button onClick={add} data-testid="caption-add-btn" className="h-8 px-3 rounded-full border border-slate-200 text-xs text-slate-700 flex items-center gap-1 hover:border-emerald-400 transition-colors"><Plus size={13} /> Add caption</button>}
        {dirty && <button onClick={save} disabled={saving} data-testid="captions-save-btn" className="h-8 px-4 rounded-full bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors disabled:opacity-60">{saving ? "Saving…" : "Save captions"}</button>}
      </div>
    </section>
  );
};

export default CaptionsCard;
